import React, { useState, useEffect, useCallback } from "react";

import {
  Grid,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import { useLocation, useNavigate } from "react-router-dom";

import debounce from "lodash.debounce";

import { useClientInfo, axiosInstance } from "../../utils/commonImports";

import TripsheetHeader from "./TripsheetHeader";
import DutyForm from "./DutyForm";
import DutyTable from "./DutyTable";
import DutyTableAll from "./DutyTableAll";

import PassengerSubform from "./PassengerSubform";
import ItinerarySubform from "./ItinerarySubform";
import ChargesForm from "./ChargesForm";
import PayoutsForm from "./PayoutsForm";
import AttachmentUploader from "./AttachmentUploader";
import {
  calculateDaysUsed,
  calculateHoursUsed,
  calculateKmsUsed,
} from "../../utils/calculations";

import { calculateTotalDuration, convertDate } from "../../utils/dateUtils";
import { createDocument } from "../../api/numberingApi";
import PreviewModal from "../../components/preview/PreviewModal";
import { useAppContext } from "../../context/AppContext";

const initialTripsheet = {
  client_id: null,
  customerid: null,
  bookingid: null,
  tripsheet_number: "",
  reference: "",
  pono: "",
  payment_mode: "",
  accounting_type: "Confirmed",
  orderbyid: null,
  orderby_name: "",
  orderby_mobile: "",
  orderby_email: "",
  customer_comments: "",
  remarks: "",
  payment_note: "",
  trip_duties: [],
};

const initialDuty = {
  duty: "",
  location: "",
  vehicleid: null,
  assigned_vehicle_group_id: null,
  dispatched_vehicle_group_id: null,
  assigned_vehicle_group_name: null,
  dispatched_vehicle_group_name: null,
  is_vendor_tripsheet: false,
  vendorid: null,
  vendor_vehicle_no: "",
  driver_id: null,
  driver_name: "",
  driver_mobile: "",
  open_date: null,
  open_time: "",
  reporting_time: "",
  close_date: null,
  close_time: "",
  open_km_reading: "",
  close_km_reading: "",
  trip_itinerary: "",
  trip_passengers: [],
  trip_itineraries: [],
};

// ============================================================
// Reusable Tripsheet Messaging recipient card
// ============================================================
function MessageRecipientCard({
  accentColor,
  roleLabel,
  name,
  mobile,
  message,
  included,
  onToggleIncluded,
  onMobileChange,
  onMessageChange,
}) {
  const missingMobile = included && !String(mobile || "").trim();
  const messageLength = String(message || "").length;

  return (
    <Box
      sx={{
        width: 260,
        border: "1px solid",
        borderColor: included ? accentColor : "#ddd",
        borderTop: `4px solid ${included ? accentColor : "#ccc"}`,
        borderRadius: 2,
        p: 1.2,
        flexShrink: 0,
        bgcolor: included ? "#fff" : "#f5f5f5",
        opacity: included ? 1 : 0.6,
        transition: "all 0.15s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            color: accentColor,
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          {roleLabel}
        </Typography>
        <Checkbox
          size="small"
          checked={included}
          onChange={(e) => onToggleIncluded(e.target.checked)}
          sx={{ p: 0.3 }}
        />
      </Box>

      <Typography
        sx={{ fontSize: "13px", fontWeight: 600, mb: 0.8 }}
        noWrap
        title={name || ""}
      >
        {name || "—"}
      </Typography>

      <TextField
        fullWidth
        size="small"
        label="Mobile Number"
        value={mobile || ""}
        onChange={(e) => onMobileChange(e.target.value)}
        error={missingMobile}
        helperText={missingMobile ? "Mobile number required to send" : " "}
        disabled={!included}
        sx={{
          mb: 0.5,
          "& .MuiInputBase-input": { fontSize: "11px" },
          "& .MuiInputLabel-root": { fontSize: "11px" },
          "& .MuiFormHelperText-root": {
            fontSize: "10px",
            m: 0,
            minHeight: "14px",
          },
        }}
      />

      <TextField
        fullWidth
        multiline
        minRows={10}
        maxRows={12}
        label="Message"
        value={message || ""}
        onChange={(e) => onMessageChange(e.target.value)}
        disabled={!included}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: "11px",
            lineHeight: 1.4,
          },
          "& .MuiInputLabel-root": { fontSize: "11px" },
        }}
      />
      <Typography
        sx={{
          fontSize: "10px",
          color: messageLength > 1024 ? "error.main" : "text.secondary",
          textAlign: "right",
          mt: 0.3,
        }}
      >
        {messageLength} characters
      </Typography>
    </Box>
  );
}

export default function TripsheetFormAdd({
  editData: editDataProp,
  bookingId: bookingIdProp,
  onCancel,
  onSuccess,
}) {
    const { clientId, client: clientInfo } = useClientInfo();
  const { fyid } = useAppContext();

  const helpline = clientInfo?.phone || "";

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewData, setPreviewData] = useState(null);

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);

  const [messageRecipients, setMessageRecipients] = useState({
    users: [],
    orderBy: null,
    driver: null,
    vendor: null,
  });

  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  const autoPreviewShownRef = React.useRef(false);
  const pendingMessageRecipientsRef = React.useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  // ============================================================
  // EDIT DATA CAN COME FROM:
  // 1. Tripsheet page Edit button -> React prop
  // 2. BookingForm Tripsheet Number -> location.state
  // ============================================================
  const editData = editDataProp || location.state?.editData || null;

  const bookingId =
    bookingIdProp ||
    location.state?.bookingId ||
    new URLSearchParams(location.search).get("bookingid") ||
    null;

  const tripsheetId =
    location.state?.tripsheetId ||
    new URLSearchParams(location.search).get("tripsheetid") ||
    null;

  const fromBooking = location.state?.fromBooking === true;

  const [form, setForm] = useState(initialTripsheet);
  const [dutyForm, setDutyForm] = useState(initialDuty);
  const chargesRef = React.useRef(null);
  const payoutsRef = React.useRef(null);
  const customerId = form?.customerid || null;
  const [dutyEditIndex, setDutyEditIndex] = useState(-1);
  const [dropdownData, setDropdownData] = useState({
    customers: [],
    vehicles: [],
    vendors: [],
    vehicleGroups: [],
    locations: [],
    drivers: [],
    orderbyContacts: [],
    usedByContacts: [],
  });

  const [tripsheetSettings, setTripsheetSettings] = useState({
    duty_entry_type: "SINGLE",
    carry_forward_opening_km: false,
    multiple_others_entry: false,
    multiple_permit_entry: false,
    parking_others_permit_to_vehicle: false,
    check_vehicle_documents_expiry: false,
    check_tripsheets_returned: false,
    enable_print: true,
    template_id: null,
    print_client_name_address_vendor_vehicle: false,
    send_message_to_users: false,
    send_message_to_driver: false,
    send_message_to_vendor: false,
  });

  const [tripsheetSettingsLoaded, setTripsheetSettingsLoaded] = useState(false);

  const dutyEntryType = tripsheetSettings?.duty_entry_type || "SINGLE";

  const isMultipleDuty = dutyEntryType === "MULTIPLE";

  // Format hours and minutes into HH:MM
  const formatUsageHHMM = ({ hours, minutes }) => {
    const pad = (n) => (n < 10 ? "0" + n : n);
    return `${pad(hours)}:${pad(minutes)}`;
  };

  const [passengers, setPassengers] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [payoutsDuty, setPayoutsDuty] = useState("");
  const [payoutsDutyType, setPayoutsDutyType] = useState(dutyForm.duty);
  const [payoutsVehicleGroupId, setPayoutsVehicleGroupId] = useState(
    dutyForm.assigned_vehicle_group_id,
  );
  const [tripPayoutsDaysUsed, setTripPayoutsDaysUsed] = useState(0);
  const [tripPayoutsHoursUsed, setTripPayoutsHoursUsed] = useState(0);
  const [tripPayoutsKmsUsed, setTripPayoutsKmsUsed] = useState(0);
  const [tripChargesDaysUsed, setTripChargesDaysUsed] = useState(0);
  const [tripChargesHoursUsed, setTripChargesHoursUsed] = useState(0);
  const [tripChargesKmsUsed, setTripChargesKmsUsed] = useState(0);

  const [tariff, setTariff] = useState(null);
  const [tariffSlabs, setTariffSlabs] = useState([]);
  const [usageTimeRoundoff, setUsageTimeRoundoff] = useState(null);

  const [hoursDisplay, setHoursDisplay] = useState({ hours: 0, minutes: 0 });
  const hoursUsedDisplayString = formatUsageHHMM(hoursDisplay);
  const [selectedDutyType, setSelectedDutyType] = useState("");
  const [selectedVehicleGroupId, setSelectedVehicleGroupId] = useState("");
  // const [resetChargesSignal, setResetChargesSignal] = useState(0);
  // const [resetPayoutsSignal, setResetPayoutsSignal] = useState(0);
  const [selectedVehicleGroup, setSelectedVehicleGroup] = useState("");
  const [showChargesAndPayouts, setShowChargesAndPayouts] = useState(true);
  const [payoutsTariff, setPayoutsTariff] = useState(null);
  const [payoutsTariffSlabs, setPayoutsTariffSlabs] = useState([]);
  const [payoutsUsageTimeRoundoff, setPayoutsUsageTimeRoundoff] =
    useState(null);

  const vendorName =
    dropdownData?.vendors?.find(
      (v) => v != null && String(v.vendorid) === String(dutyForm.vendorid),
    )?.vendorname || "";

  const vehicleNumber =
    dropdownData?.vehicles?.find((v) => v.vehicle_id === dutyForm.vehicleid)
      ?.vehicle_number || "";

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [dutyDateError, setDutyDateError] = useState("");

  const validateDutyDates = (openDate, closeDate) => {
    if (!openDate || !closeDate) return "";

    const open = new Date(openDate);
    const close = new Date(closeDate);
    open.setHours(0, 0, 0, 0);
    close.setHours(0, 0, 0, 0);

    if (close < open) {
      return "Close date cannot be earlier than open date";
    }
    return "";
  };

  useEffect(() => {
    if (!clientId) return;

    const loadTripsheetSettings = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/clients/${clientId}/tripsheet-settings`,
        );
        const settings = response?.data?.tripsheet;

        setTripsheetSettings({
          duty_entry_type:
            settings?.duty_entry_type === "MULTIPLE" ? "MULTIPLE" : "SINGLE",
          carry_forward_opening_km: settings?.carry_forward_opening_km === true,
          multiple_others_entry: settings?.multiple_others_entry === true,
          multiple_permit_entry: settings?.multiple_permit_entry === true,
          check_vehicle_documents_expiry:
            settings?.check_vehicle_documents_expiry === true,
          parking_others_permit_to_vehicle:
            settings?.parking_others_permit_to_vehicle === true,
          check_tripsheets_returned:
            settings?.check_tripsheets_returned === true,

          enable_print: settings?.enable_print !== false,

          template_id: settings?.template_id ?? null,

          print_client_name_address_vendor_vehicle:
            settings?.print_client_name_address_vendor_vehicle === true,
          send_message_to_users: settings?.send_message_to_users === true,

          send_message_to_driver: settings?.send_message_to_driver === true,

          send_message_to_vendor: settings?.send_message_to_vendor === true,
        });
      } catch (error) {
        console.error("Failed to load Tripsheet settings:", error);

        // Database default is SINGLE
        setTripsheetSettings({
          duty_entry_type: "SINGLE",
          carry_forward_opening_km: false,
          multiple_others_entry: false,
          multiple_permit_entry: false,
          parking_others_permit_to_vehicle: false,
          check_vehicle_documents_expiry: false,
          check_tripsheets_returned: false,
          enable_print: true,
          template_id: null,
          print_client_name_address_vendor_vehicle: false,
          send_message_to_users: false,
          send_message_to_driver: false,
          send_message_to_vendor: false,
        });
      } finally {
        setTripsheetSettingsLoaded(true);
      }
    };

    loadTripsheetSettings();
  }, [clientId]);

  /* ============================================================
   LOAD EXISTING TRIPSHEET BY ID
   Used when Tripsheet Number is clicked from BookingForm
============================================================ */
  useEffect(() => {
    // ==========================================================
    // THIS API LOADER IS ONLY FOR:
    // BookingForm → Click Tripsheet Number
    //
    // Direct Tripsheet Edit uses editData instead.
    // ==========================================================
    if (!clientId || !tripsheetId || !fromBooking) {
      return;
    }

    let cancelled = false;

    const loadTripsheet = async () => {
      try {
        console.log("🚕 Loading existing Tripsheet:", tripsheetId);

        const response = await axiosInstance.get(
          `/api/tripsheets/${tripsheetId}?client_id=${clientId}`,
        );

        const data = response.data;

        console.log("🚕 Existing Tripsheet received:", data);

        if (!data || cancelled) {
          return;
        }

        /*
         * Some APIs return:
         * {
         *   tripsheet: {...},
         *   trip_duties: [...],
         *   passengers: [...],
         *   itineraries: [...],
         *   charges: [...],
         *   payouts: [...]
         * }
         *
         * If API directly returns the tripsheet object,
         * normalize it here as well.
         */

        const tripsheetData = data.tripsheet || data;

        // ==========================================================
        // MAP DUTIES WITH THEIR CHARGES AND PAYOUTS
        // ==========================================================

        const mappedDuties = (data.trip_duties || []).map((duty) => ({
          ...duty,

          charges:
            (data.charges || []).find(
              (ch) => String(ch.trip_duty_id) === String(duty.trip_duty_id),
            ) || {},

          payouts:
            (data.payouts || []).find(
              (po) => String(po.trip_duty_id) === String(duty.trip_duty_id),
            ) || {},
        }));

        console.log("🚕 Existing duties mapped:", mappedDuties);

        // ==========================================================
        // SET MAIN TRIPSHEET FORM
        // ==========================================================

        setForm({
          ...initialTripsheet,
          ...tripsheetData,

          tripsheetid: tripsheetData.tripsheetid || tripsheetId,

          bookingid: tripsheetData.bookingid || bookingId || null,

          client_id: clientId,

          /*
           * IMPORTANT:
           * Existing duties remain in form.trip_duties.
           * This is what displays them in the Duty Table.
           */
          trip_duties: mappedDuties,

          passengers: Array.isArray(data.passengers) ? data.passengers : [],

          itineraries: Array.isArray(data.itineraries) ? data.itineraries : [],

          charges: Array.isArray(data.charges) ? data.charges : [],

          payouts: Array.isArray(data.payouts) ? data.payouts : [],

          attachments: Array.isArray(data.attachments) ? data.attachments : [],
        });

        // ==========================================================
        // CUSTOMER
        // ==========================================================

        const customerObj = Array.isArray(dropdownData.customers)
          ? dropdownData.customers.find(
              (c) => String(c.customerid) === String(tripsheetData.customerid),
            )
          : null;

        if (customerObj) {
          setCustomerName(customerObj.customername || "");
        }

        // ==========================================================
        // IMPORTANT DUTY FORM BEHAVIOUR
        //
        // Existing duties should appear ONLY in the table.
        //
        // DutyForm must remain EMPTY / ADD DUTY mode.
        // User can click the table EDIT icon later
        // if they want to edit an existing duty.
        // ==========================================================

        console.log("🚕 Existing duties loaded into table:", mappedDuties);

        setDutyForm({
          ...initialDuty,
        });

        /*
         * -1 means ADD DUTY mode.
         *
         * Therefore button will show:
         * +ADD DUTY
         */
        setDutyEditIndex(-1);

        /*
         * Clear selected duty fields
         */
        setSelectedDutyType("");

        setSelectedVehicleGroupId("");

        /*
         * Clear payout selections
         */
        setPayoutsDutyType("");

        setPayoutsVehicleGroupId("");

        console.log("✅ DutyForm reset to ADD DUTY mode");

        // ==========================================================
        // PASSENGERS
        // ==========================================================

        if (Array.isArray(data.passengers)) {
          const normalizedPassengers = data.passengers.map((p) => ({
            ...p,

            id: p.id ?? p.passengerid ?? p.dbId ?? null,

            used_by_id:
              p.used_by_id ||
              p.used_contact?.contactid ||
              p.used_contact?.customercontactid ||
              null,

            used_by_title:
              p.used_by_title ||
              p.used_title ||
              p.used_contact?.customercontactsalutation ||
              "",

            used_title: p.used_title || p.used_by_title || "",

            used_name: p.used_name || p.used_by_name || "",

            used_mobile: p.used_mobile || p.used_by_mobile || "",

            used_email: p.used_email || p.used_by_email || "",

            used_department: p.used_department || p.used_by_department || "",

            report_location_id: p.report_location_id ?? null,

            report_address: p.report_address || "",

            drop_location_id: p.drop_location_id ?? null,

            drop_address: p.drop_address || "",
          }));

          setPassengers(normalizedPassengers);
        } else {
          setPassengers([]);
        }

        // ==========================================================
        // ITINERARIES
        // ==========================================================

        setItineraries(Array.isArray(data.itineraries) ? data.itineraries : []);

        // ==========================================================
        // ATTACHMENTS
        // ==========================================================

        setAttachments(Array.isArray(data.attachments) ? data.attachments : []);

        console.log("✅ Existing Tripsheet loaded successfully");
      } catch (error) {
        console.error(
          "❌ Failed to load existing Tripsheet:",
          error?.response?.data || error,
        );

        if (!cancelled) {
          setSnackbar({
            open: true,
            message:
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              "Failed to load tripsheet details",
            severity: "error",
          });
        }
      }
    };

    loadTripsheet();

    return () => {
      cancelled = true;
    };
  }, [clientId, tripsheetId, bookingId, fromBooking]);

  // ✅ Initialize payoutsDutyType and payoutsVehicleGroupId only once

  useEffect(() => {
    if (!payoutsDutyType) {
      const initialDuty =
        dutyForm?.payouts?.trip_payouts_duty || dutyForm?.duty || "";
      setPayoutsDutyType(initialDuty);
    }
    if (!payoutsVehicleGroupId) {
      const initialVG = dutyForm?.vehicle_group_id || "";
      setPayoutsVehicleGroupId(initialVG);
    }
  }, []);

  // 🧩 Keep dutyForm.payouts updated when payout duty type changes
  useEffect(() => {
    if (!dutyForm) return;
    setDutyForm((prev) => ({
      ...prev,
      payouts: {
        ...prev.payouts,
        trip_payouts_duty: payoutsDutyType,
      },
    }));
  }, [payoutsDutyType]);

  // Usage and tariff calculation effect
  useEffect(() => {
    if (
      !dutyForm?.duty ||
      !dutyForm?.assigned_vehicle_group_id ||
      !customerId
    ) {
      setTariff(null);
      setTariffSlabs([]);
      setTripChargesDaysUsed(0);
      setTripChargesHoursUsed(0);
      setTripChargesKmsUsed(0);
      return;
    }

    const dayBasedDuties = ["Outstation", "OSDayRate", "Package"];
    const isDayBased = dayBasedDuties.includes(dutyForm.duty ?? "");
    const daysUsed = calculateDaysUsed(dutyForm.open_date, dutyForm.close_date);
    const usage = calculateTotalDuration(
      dutyForm.open_date,
      dutyForm.open_time,
      dutyForm.close_date,
      dutyForm.close_time,
    );
    const decimalHours = usage?.totalHours ?? 0;
    const kmsUsed = calculateKmsUsed(
      dutyForm.open_km_reading,
      dutyForm.close_km_reading,
    );

    if (isDayBased) {
      setTripChargesDaysUsed(daysUsed);
      setTripChargesHoursUsed(0);
    } else {
      setTripChargesDaysUsed(0);
      setTripChargesHoursUsed(decimalHours);
    }
    setTripChargesKmsUsed(kmsUsed);

    async function fetchTariffData() {
      try {
        const payload = {
          entity_type: "customer",
          entity_id: customerId,
          client_id: clientId,
          locationid: dutyForm.location || null,
          vehicle_group_id: dutyForm.assigned_vehicle_group_id,
          duty_type: dutyForm.duty,
          hours_used: isDayBased ? daysUsed : decimalHours,

          // ⭐ NEW — REQUIRED FOR INDIVIDUAL TARIFF
          vehicle_id: dutyForm.vehicleid ? Number(dutyForm.vehicleid) : null,
          vendor_id: dutyForm.vendorid ? Number(dutyForm.vendorid) : null,
        };

        const response = await axiosInstance.post(
          "/api/tripsheets/fetch-tariff",
          payload,
        );
        setTariff(response.data.tariff || null);
        setTariffSlabs(response.data.slab ? [response.data.slab] : []);
        setUsageTimeRoundoff(response.data.usage_time_roundoff || null);
      } catch {
        setTariff(null);
        setTariffSlabs([]);
      }
    }
    fetchTariffData();
  }, [
    dutyForm.duty,
    dutyForm.assigned_vehicle_group_id,
    dutyForm.location,
    dutyForm.open_date,
    dutyForm.open_time,
    dutyForm.close_date,
    dutyForm.close_time,
    dutyForm.open_km_reading,
    dutyForm.close_km_reading,
    customerId,
    clientId,
  ]);

  useEffect(() => {
    console.log("→ payouts tariff effect fired", {
      payoutsDutyType,
      payoutsVehicleGroupId,
      dutyFormDuty: dutyForm?.duty,
      dutyFormAssignedVehicleGroup: dutyForm?.assigned_vehicle_group_id,
      customerId,
    });

    if (!payoutsDutyType || !payoutsVehicleGroupId || !customerId) {
      setPayoutsTariff(null);
      setPayoutsTariffSlabs([]);

      setPayoutsUsageTimeRoundoff(null);

      setTripPayoutsDaysUsed(0);
      setTripPayoutsHoursUsed(0);
      setTripPayoutsKmsUsed(0);

      return;
    }

    const dayBasedDuties = ["Outstation", "OSDayRate", "Package"];
    const isDayBased = dayBasedDuties.includes(payoutsDutyType ?? "");
    const daysUsed = calculateDaysUsed(dutyForm.open_date, dutyForm.close_date);
    const usage = calculateTotalDuration(
      dutyForm.open_date,
      dutyForm.open_time,
      dutyForm.close_date,
      dutyForm.close_time,
    );
    const decimalHours = usage?.totalHours ?? 0;
    const kmsUsed = calculateKmsUsed(
      dutyForm.open_km_reading,
      dutyForm.close_km_reading,
    );

    if (isDayBased) {
      setTripPayoutsDaysUsed(daysUsed);
      setTripPayoutsHoursUsed(0);
    } else {
      setTripPayoutsDaysUsed(0);
      setTripPayoutsHoursUsed(decimalHours);
    }
    setTripPayoutsKmsUsed(kmsUsed);

    //   async function fetchPayoutsTariffData() {
    //     try {
    //       console.log("🔍 dutyForm vendor field check:", dutyForm);

    //       const payload = {
    //         entity_type: "customer", // sales side always customer
    //         entity_id: customerId,
    //         client_id: clientId,
    //         locationid: dutyForm.location || null,

    //         // ⭐ IMPORTANT: vendor tariff uses vendor vehicle group (dispatched), NOT assigned
    //         vehicle_group_id: dutyForm.dispatched_vehicle_group_id,

    //         duty_type: payoutsDutyType,
    //         hours_used: isDayBased ? daysUsed : decimalHours,

    //         // ⭐ Vendor Side Tariff Fetch
    //         vendor_id: dutyForm.vendorid ? Number(dutyForm.vendorid) : null,

    //         // ⭐ Vehicle ID (if vendor entered specific vehicle no)
    //         vehicle_id: dutyForm.vehicleid ? Number(dutyForm.vehicleid) : null,
    //       };

    //       const response = await axiosInstance.post(
    //         "/api/tripsheets/fetch-tariff",
    //         payload,
    //       );
    //       setPayoutsTariff(response.data.tariff || null);
    //       setPayoutsTariffSlabs(response.data.slab ? [response.data.slab] : []);
    //       setPayoutsUsageTimeRoundoff(response.data.usage_time_roundoff || null);

    //       console.log(
    //         "✅ payouts tariff set:",
    //         response.data.tariff,
    //         response.data.slab,
    //       );
    //     } catch {
    //       setPayoutsTariff(null);
    //       setPayoutsTariffSlabs([]);
    //     }
    //   }
    //   fetchPayoutsTariffData();
    // }, [
    //   payoutsDutyType,
    //   payoutsVehicleGroupId,
    //   dutyForm.location,
    //   dutyForm.open_date,
    //   dutyForm.open_time,
    //   dutyForm.close_date,
    //   dutyForm.close_time,
    //   dutyForm.open_km_reading,
    //   dutyForm.close_km_reading,
    //   customerId,
    //   clientId,
    // ]);

    async function fetchPayoutsTariffData() {
      try {
        console.log("🔍 dutyForm vendor field check:", dutyForm);

        const payload = {
          entity_type: "customer", // sales side always customer
          entity_id: customerId,
          client_id: clientId,
          locationid: dutyForm.location || null,

          // ⭐ IMPORTANT: payout tariff uses dispatched vehicle group
          vehicle_group_id: dutyForm.dispatched_vehicle_group_id,

          duty_type: payoutsDutyType,

          // ⭐ Slab selection hours
          hours_used: isDayBased ? daysUsed : Math.floor(decimalHours),

          // ⭐ Actual usage hours for extra-time round-off
          actual_hours_used: isDayBased ? daysUsed : decimalHours,

          // ⭐ Payout round-off uses Vehicle rules
          roundoff_entity: "Vehicle",
          roundoff_entity_id: dutyForm.vehicleid
            ? Number(dutyForm.vehicleid)
            : null,

          // ⭐ Vendor ID
          vendor_id: dutyForm.vendorid ? Number(dutyForm.vendorid) : null,

          // ⭐ Vehicle ID
          vehicle_id: dutyForm.vehicleid ? Number(dutyForm.vehicleid) : null,
        };
        const response = await axiosInstance.post(
          "/api/tripsheets/fetch-tariff",
          payload,
        );

        setPayoutsTariff(response.data.tariff || null);
        setPayoutsTariffSlabs(response.data.slab ? [response.data.slab] : []);

        // ⭐ Usage Time Round-Off from backend
        setPayoutsUsageTimeRoundoff(response.data.usage_time_roundoff || null);

        console.log(
          "✅ payouts tariff set:",
          response.data.tariff,
          response.data.slab,
          response.data.usage_time_roundoff,
        );
      } catch {
        setPayoutsTariff(null);
        setPayoutsTariffSlabs([]);

        // ⭐ Clear round-off also
        setPayoutsUsageTimeRoundoff(null);
      }
    }

    fetchPayoutsTariffData();
  }, [
    payoutsDutyType,
    payoutsVehicleGroupId,
    dutyForm.location,
    dutyForm.open_date,
    dutyForm.open_time,
    dutyForm.close_date,
    dutyForm.close_time,
    dutyForm.open_km_reading,
    dutyForm.close_km_reading,
    customerId,
    clientId,
  ]);

  useEffect(() => {
    console.log(
      "🟣 [InitEffect] Checking initial payoutsDuty & payoutsVehicleGroupId",
    );

    // Initialize only if payoutsDuty is still empty
    if (!payoutsDuty && dutyForm?.duty) {
      console.log("🔄 Initializing payoutsDuty →", dutyForm.duty);
      setPayoutsDuty(dutyForm.duty);
    }

    // Initialize only if payoutsVehicleGroupId is still empty
    if (!payoutsVehicleGroupId && dutyForm?.vehicle_group_id) {
      console.log(
        "🔄 Initializing payoutsVehicleGroupId →",
        dutyForm.vehicle_group_id,
      );
      setPayoutsVehicleGroupId(dutyForm.vehicle_group_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchDropdowns() {
      try {
        const [
          custRes,
          vehRes,
          venRes,
          vgRes,
          locRes,
          driverRes,
          orderByRes,
          usedByRes,
        ] = await Promise.all([
          axiosInstance.get("/api/customers", {
            params: { client_id: clientId },
          }),
          axiosInstance.get("/api/vehicles", {
            params: { client_id: clientId },
          }),
          axiosInstance.get("/api/vendors", {
            params: { client_id: clientId },
          }),
          axiosInstance.get("/api/components/category", {
            params: { type: "VehicleGroup", client_id: clientId },
          }),
          axiosInstance.get("/api/components/category", {
            params: { type: "Location", client_id: clientId },
          }),
          axiosInstance.get("/api/employees", {
            params: { client_id: clientId, employeedrivertype: "Driver" },
          }),
          axiosInstance.get("/api/customercontacts", {
            params: {
              contact_type: "Orderby",
              customerid: form.customerid,
              client_id: clientId,
            },
          }),
          axiosInstance.get("/api/customercontacts", {
            params: {
              contact_type: "Usedby",
              customerid: form.customerid,
              client_id: clientId,
            },
          }),
        ]);

        setDropdownData({
          customers: custRes.data,
          vehicles: vehRes.data,
          vendors: venRes.data,
          vehicleGroups: vgRes.data,
          locations: locRes.data,
          drivers: driverRes.data,
          orderbyContacts: orderByRes.data,
          usedByContacts: usedByRes.data,
        });
      } catch {
        setSnackbar({
          open: true,
          message: "Failed to load dropdown data",
          severity: "error",
        });
      }
    }
    if (clientId) fetchDropdowns();
  }, [clientId, form.customerid]);

  useEffect(() => {
    if (!editData || !editData.tripsheet) {
      return;
    }

    console.log("🔴 DIRECT EDIT useEffect RUNNING", {
      fromBooking,
      editData,
      tripsheetId,
    });

    // ============================================================
    // CUSTOMER
    // ============================================================

    const customerObj = Array.isArray(dropdownData.customers)
      ? dropdownData.customers.find(
          (c) => String(c.customerid) === String(editData.tripsheet.customerid),
        )
      : null;

    setCustomerName(customerObj ? customerObj.customername : "");

    // ============================================================
    // MAP EXISTING DUTIES
    // ============================================================

    const mappedDuties = (editData.trip_duties || []).map((duty) => ({
      ...duty,

      charges:
        (editData.charges || []).find(
          (ch) => String(ch.trip_duty_id) === String(duty.trip_duty_id),
        ) || {},

      payouts:
        (editData.payouts || []).find(
          (po) => String(po.trip_duty_id) === String(duty.trip_duty_id),
        ) || {},
    }));

    console.log("Mapped duties for direct Tripsheet Edit:", mappedDuties);

    // ============================================================
    // ORDER BY CONTACT
    // ============================================================

    const contact = dropdownData.orderbyContacts
      ? dropdownData.orderbyContacts.find(
          (c) => String(c.contactid) === String(editData.tripsheet.orderbyid),
        )
      : null;

    // ============================================================
    // LOAD MAIN TRIPSHEET FORM
    // ============================================================

    setForm({
      ...initialTripsheet,
      ...editData.tripsheet,

      orderbyid: editData.tripsheet.orderbyid || "",

      orderby_title: contact?.customercontactsalutation || "",

      orderby_name: contact
        ? `${contact.customercontactfirstname || ""} ${
            contact.customercontactlastname || ""
          }`.trim()
        : "",

      orderby_mobile: contact?.customercontactmobile || "",

      orderby_email: contact?.customercontactemail || "",

      orderby_department: contact?.customercontactdepartment || "",

      // Existing duties go to Duty TABLE
      trip_duties: mappedDuties,

      passengers: Array.isArray(editData.passengers) ? editData.passengers : [],

      itineraries: Array.isArray(editData.itineraries)
        ? editData.itineraries
        : [],

      charges: Array.isArray(editData.charges) ? editData.charges : [],

      payouts: Array.isArray(editData.payouts) ? editData.payouts : [],

      attachments: Array.isArray(editData.attachments)
        ? editData.attachments
        : [],
    });

    // ============================================================
    // IMPORTANT:
    // DO NOT LOAD FIRST DUTY INTO DUTY FORM
    //
    // When opening directly from Tripsheet List,
    // DutyForm must remain EMPTY.
    //
    // User will click Edit in Duty Table to load a duty.
    // ============================================================

    setDutyForm({
      ...initialDuty,
      charges: {},
      payouts: {},
    });

    setDutyEditIndex(-1);

    setSelectedDutyType("");

    setSelectedVehicleGroupId("");

    setPayoutsDutyType("");

    setPayoutsVehicleGroupId("");

    setShowChargesAndPayouts(false);

    console.log("✅ Direct Tripsheet Edit loaded successfully");

    console.log("✅ Header loaded");

    console.log("✅ Duty table loaded:", mappedDuties.length);

    console.log("✅ DutyForm reset and kept empty");
  }, [editData, dropdownData.customers, dropdownData.orderbyContacts]);

  useEffect(() => {
    if (editData && Array.isArray(editData.passengers)) {
      const normalized = editData.passengers.map((p) => ({
        id: p.id ?? p.passengerid ?? p.dbId ?? null,

        used_by_id:
          p.used_by_id ||
          p.used_contact?.contactid ||
          p.used_contact?.customercontactid ||
          null,

        used_by_title:
          p.used_by_title ||
          p.used_title ||
          p.used_contact?.customercontactsalutation ||
          "",

        // PassengerSubform uses these fields
        used_title: p.used_title || p.used_by_title || "",

        used_name: p.used_name || p.used_by_name || "",

        used_mobile: p.used_mobile || p.used_by_mobile || "",

        used_email: p.used_email || p.used_by_email || "",

        used_department: p.used_department || p.used_by_department || "",

        report_location_id: p.report_location_id ?? null,

        report_address: p.report_address || "",

        drop_location_id: p.drop_location_id ?? null,

        drop_address: p.drop_address || "",
      }));

      console.log("Normalized passengers:", normalized);

      setPassengers(normalized);
    }
  }, [editData]);

  /* ============================================================
   LOAD BOOKING WHEN GENERATING TRIPSHEET FROM BOOKING
============================================================ */
  useEffect(() => {
    if (!clientId || !bookingId || editData?.tripsheet) {
      return;
    }

    let cancelled = false;

    const loadBookingForTripsheet = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/bookings/${bookingId}?client_id=${clientId}`,
        );

        const booking = response.data;

        if (!booking || cancelled) {
          return;
        }

        /* ======================================================
         CUSTOMER
      ====================================================== */
        const customerObj = Array.isArray(dropdownData.customers)
          ? dropdownData.customers.find(
              (c) => String(c.customerid) === String(booking.customer_id),
            )
          : null;

        /* ======================================================
         ORDER BY CONTACT
      ====================================================== */
        const orderByContact = Array.isArray(dropdownData.orderbyContacts)
          ? dropdownData.orderbyContacts.find(
              (c) => String(c.contactid) === String(booking.order_by_id),
            )
          : null;

        const orderByName =
          booking.order_by_name ||
          (orderByContact
            ? `${orderByContact.customercontactfirstname || ""} ${
                orderByContact.customercontactlastname || ""
              }`.trim()
            : "");

        const orderByMobile =
          booking.order_by_mobile ||
          orderByContact?.customercontactmobile ||
          "";

        const orderByEmail =
          booking.order_by_email || orderByContact?.customercontactemail || "";

        /* ======================================================
         BUILD THE DUTY — SINGLE SOURCE OF TRUTH
         This same object is used for BOTH:
           - form.trip_duties[0]   (shown in Duty Table)
           - dutyForm              (shown in Duty Form)
         Do not build a second, divergent version later.
      ====================================================== */
        const bookingDuty = {
          duty: booking.duty_type || "",

          // Location comes from Booking Location (category id),
          // NOT booking.booking_from (which is a display name like "Mail").
          location: booking.location_id || null,

          vehicleid: booking.vehicle_id || null,

          assigned_vehicle_group_id: booking.vehicle_group_id || null,

          // NOTE: confirm with product/business owner whether
          // "dispatched" vehicle group should default to the
          // booking's vehicle group, or stay null until an
          // actual dispatch action happens. Currently defaulting
          // to booking.vehicle_group_id to match prior behaviour.
          dispatched_vehicle_group_id: booking.vehicle_group_id || null,

          assigned_vehicle_group_name: null,
          dispatched_vehicle_group_name: null,

          is_vendor_tripsheet: Boolean(booking.is_vendor_booking),

          vendorid: booking.vendor_id || null,
          vendor_vehicle_no: booking.vendor_vehicle_number || "",

          driver_id: booking.driver_id ? Number(booking.driver_id) : null,
          driver_name: booking.driver_name || "",
          driver_mobile: booking.driver_mobile || "",

          // Booking reporting date/time become planned open date/time
          open_date: booking.reporting_date || null,
          open_time: booking.reporting_time || "",
          reporting_time: booking.reporting_time || "",

          // Booking release date/time become planned close date/time
          close_date: booking.release_date || null,
          close_time: booking.release_time || "",

          open_km_reading: "",
          close_km_reading: "",

          trip_itinerary: "",
          trip_passengers: [],
          trip_itineraries: [],

          charges: {},
          payouts: {},
        };

        /* ======================================================
         SET TRIPSHEET HEADER + BOOKING DUTY
      ====================================================== */
        setForm((prev) => ({
          ...prev,

          bookingid: booking.bookingid,
          client_id: clientId,

          customerid: booking.customer_id || null,

          orderbyid: booking.order_by_id || orderByContact?.contactid || null,

          orderby_name: orderByName,
          orderby_mobile: orderByMobile,
          orderby_email: orderByEmail,

          orderby_title:
            booking.order_by_title ||
            orderByContact?.customercontactsalutation ||
            "",

          reference: booking.reference || "",

          payment_mode: booking.payment_mode || "",
          accounting_type:
            booking.accounting_type === "Estimation" ||
            location.state?.accounting_type === "Estimation"
              ? "Estimation"
              : "Confirmed",

          customer_comments: booking.customer_comments || "",

          remarks: booking.remarks || "",

          tripsheet_number: "",

          // Booking duty becomes the Tripsheet duty (Duty Table)
          trip_duties: [bookingDuty],
        }));

        // The duty currently shown in DutyForm is the same duty
        // already stored in form.trip_duties[0]. Do not rebuild it.
        setDutyForm(bookingDuty);
        setDutyEditIndex(0);

        setSelectedDutyType(booking.duty_type || "");
        setSelectedVehicleGroupId(booking.vehicle_group_id || "");

        /* ======================================================
         CUSTOMER NAME
      ====================================================== */
        if (customerObj) {
          setCustomerName(customerObj.customername || "");
        }

        /* ======================================================
         PASSENGERS
         Load passenger details from Booking and resolve
         Used By contact details using used_by_id
      ====================================================== */
        const bookingPassengers = Array.isArray(booking.passengers)
          ? booking.passengers
          : [];

        let usedByContacts = [];

        try {
          const usedByResponse = await axiosInstance.get(
            `/api/customercontacts?client_id=${clientId}&customerid=${booking.customer_id}&contact_type=Usedby`,
          );

          usedByContacts = Array.isArray(usedByResponse.data)
            ? usedByResponse.data
            : [];
        } catch (contactError) {
          console.error(
            "Failed to load Used By contacts:",
            contactError?.response?.data || contactError,
          );
          usedByContacts = [];
        }

        /* ======================================================
         NORMALIZE + RESOLVE PASSENGERS
      ====================================================== */
        const normalizedBookingPassengers = bookingPassengers.map((p) => {
          const usedById =
            p.used_by_id ??
            p.used_by ??
            p.usedby_id ??
            p.usedbyid ??
            p.used_contact?.contactid ??
            p.used_contact?.customercontactid ??
            null;

          const contact = usedByContacts.find(
            (c) =>
              String(c.contactid) === String(usedById) ||
              String(c.customercontactid) === String(usedById),
          );

          const contactName = contact
            ? `${contact.customercontactfirstname || ""} ${
                contact.customercontactlastname || ""
              }`.trim()
            : "";

          const passengerName =
            p.used_by_name || p.used_name || contactName || "";

          const passengerMobile =
            p.used_by_mobile ||
            p.used_mobile ||
            contact?.customercontactmobile ||
            "";

          const passengerEmail =
            p.used_by_email ||
            p.used_email ||
            contact?.customercontactemail ||
            "";

          const passengerTitle =
            p.used_by_title ||
            p.used_title ||
            contact?.customercontactsalutation ||
            contact?.customercontacttitle ||
            "";

          const passengerDepartment =
            p.used_by_department ||
            p.used_department ||
            contact?.customercontactdepartment ||
            "";

          return {
            ...p,

            id: p.id ?? p.passengerid ?? p.dbId ?? null,

            used_by_id: usedById,

            used_by_title: passengerTitle,
            used_title: passengerTitle,

            used_name: passengerName,
            used_mobile: passengerMobile,
            used_email: passengerEmail,
            used_department: passengerDepartment,

            report_location_id: p.report_location_id ?? null,
            report_address: p.report_address || "",

            drop_location_id: p.drop_location_id ?? null,
            drop_address: p.drop_address || "",
          };
        });

        setPassengers(normalizedBookingPassengers);

        /* ======================================================
         ITINERARIES
      ====================================================== */
        setItineraries(
          Array.isArray(booking.itineraries) ? booking.itineraries : [],
        );
      } catch (error) {
        console.error(
          "Failed to load booking for Tripsheet:",
          error?.response?.data || error,
        );

        if (!cancelled) {
          setSnackbar({
            open: true,
            message:
              error?.response?.data?.error ||
              error?.response?.data?.message ||
              "Failed to load booking details",
            severity: "error",
          });
        }
      }
    };

    loadBookingForTripsheet();

    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    bookingId,
    editData,
    dropdownData.customers,
    dropdownData.orderbyContacts,
  ]);

  useEffect(() => {
    if (!clientId) return;

    const loadWhatsappTemplates = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/whatsapp/cloud/templates/${clientId}`,
        );

        if (response.data?.success) {
          setWhatsappTemplates(response.data.templates || []);
        }
      } catch (error) {
        console.error("Failed to load WhatsApp templates:", error);
        setWhatsappTemplates([]);
      }
    };

    loadWhatsappTemplates();
  }, [clientId]);

  // ============================================================
  // BUILD TRIPSHEET WHATSAPP MESSAGE
  // ============================================================
  const replaceTemplateVariables = (text, values = []) => {
    if (!text) return "";

    return text.replace(/\{\{(\d+)\}\}/g, (_, number) => {
      const index = Number(number) - 1;
      return values[index] ?? "";
    });
  };

  const getWhatsappTemplate = (templateType) => {
    const templateTypes =
      templateType === "TRIPSHEET_ORDERER"
        ? ["TRIPSHEET_ORDERER", "TRIPSHEET_ORDERBY"]
        : [templateType];

    return whatsappTemplates.find(
      (template) =>
        templateTypes.includes(template?.template_type) &&
        template?.is_active !== false,
    );
  };

  const buildTripsheetMessage = ({ templateType, values, name, mobile }) => {
    const template = getWhatsappTemplate(templateType);

    if (!template) {
      return {
        name: name || "",
        mobile: mobile || "",
        message: "",
      };
    }

    const bodyComponent = (template.components || []).find(
      (component) => component?.type === "BODY",
    );

    const message = replaceTemplateVariables(bodyComponent?.text || "", values);

    return {
      name: name || "",
      mobile: mobile || "",
      message,
      template_id: template.id,
      template_type: template.template_type,
    };
  };

  const formatMessageDate = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const formatMessageTime = (value) => {
    if (!value) return "";

    const match = String(value).match(/^(\d{1,2}):(\d{2})/);

    if (!match) return value;

    const hour = String(match[1]).padStart(2, "0");
    const minute = match[2];

    return `${hour}:${minute}`;
  };

  // ============================================================
  // PREPARE TRIPSHEET RECIPIENT MESSAGES
  // ============================================================
  const prepareTripsheetMessages = (
    normalizedDuties,
    messageClientInfo = null,
  ) => {
    const duty = normalizedDuties?.[0] || {};
    const userList = Array.isArray(passengers) ? passengers : [];
    const clientName = messageClientInfo?.name || clientInfo?.name || "";

    const helpline = messageClientInfo?.phone || clientInfo?.phone || "";

    const customerNameForMessage = customerName || form?.customer_name || "";
    // ============================================================
    // VEHICLE
    // ============================================================
    const vehicle = dropdownData?.vehicles?.find(
      (v) => String(v.vehicle_id) === String(duty.vehicleid),
    );

    const vehicleNo = duty.is_vendor_tripsheet
      ? duty.vendor_vehicle_no || ""
      : vehicle?.vehicle_number || vehicleNumber || "";

    // ============================================================
    // VEHICLE TYPE
    // ============================================================
    const vehicleType =
      duty.assigned_vehicle_group_name ||
      duty.dispatched_vehicle_group_name ||
      "";

    // ============================================================
    // TRIPSHEET NUMBER
    // ============================================================
    const tripsheetNumber = form?.tripsheet_number || "";

    // ============================================================
    // DATE / TIME
    // ============================================================
    const reportDate = formatMessageDate(duty.open_date || "");

    const reportTime = formatMessageTime(
      duty.reporting_time || duty.open_time || "",
    );

    // ============================================================
    // DRIVER
    // ============================================================
    const driverName = duty.driver_name || "";

    const driverMobile = duty.driver_mobile || "";

    // ============================================================
    // FIRST USER
    // Used for Driver / Order By / Vendor pickup information
    // ============================================================
    const firstUser = userList?.[0] || {};

    const firstUserName = firstUser?.used_name || "";

    const firstUserMobile = firstUser?.used_mobile || "";

    const firstUserPickup = firstUser?.report_address || "";

    const firstUserDrop = firstUser?.drop_address || "";

    // ============================================================
    // RESULT
    // ============================================================
    const result = {
      users: [],
      orderBy: null,
      driver: null,
      vendor: null,
    };

    // ============================================================
    // USERS
    //
    // TRIPSHEET_USER
    //
    // {{1}} Client Name
    // {{2}} User Name
    // {{3}} Tripsheet Number
    // {{4}} Vehicle Type
    // {{5}} Vehicle Number
    // {{6}} Driver Name
    // {{7}} Driver Mobile
    // {{8}} Report Date
    // {{9}} Report Time
    // {{10}} User Reporting Address
    // {{11}} User Drop Address
    // {{12}} Helpline
    // ============================================================
    if (tripsheetSettings?.send_message_to_users === true) {
      result.users = userList
        .filter((user) => user?.used_name)
        .slice(0, 3)
        .map((user) =>
          buildTripsheetMessage({
            templateType: "TRIPSHEET_USER",

            name: user.used_name,

            mobile: user.used_mobile || "",

            values: [
              clientName, // {{1}} Client Name
              user.used_name || "", // {{2}} User Name
              tripsheetNumber, // {{3}} Tripsheet Number
              vehicleType, // {{4}} Vehicle Type
              vehicleNo, // {{5}} Vehicle Number
              driverName, // {{6}} Driver Name
              driverMobile, // {{7}} Driver Mobile
              reportDate, // {{8}} Report Date
              reportTime, // {{9}} Report Time
              user.report_address || "", // {{10}} Pick-up Point
              user.drop_address || "", // {{11}} Drop Point
              helpline, // {{12}} Helpline
            ],
          }),
        );
    }

    // ============================================================
    // ORDER BY
    //
    // Uses same setting as USERS
    //
    // TRIPSHEET_ORDERER
    //
    // {{1}} Client Name
    // {{2}} Order By Name
    // {{3}} Tripsheet Number
    // {{4}} Customer Name
    // {{5}} Vehicle Type
    // {{6}} Vehicle Number
    // {{7}} Driver Name
    // {{8}} Driver Mobile
    // {{9}} Report Date
    // {{10}} Report Time
    // {{11}} Pick-up Point
    // {{12}} Drop Point
    // {{13}} Helpline
    // ============================================================
    if (tripsheetSettings?.send_message_to_users === true) {
      const orderByContact = Array.isArray(dropdownData?.orderbyContacts)
        ? dropdownData.orderbyContacts.find(
            (contact) => String(contact.contactid) === String(form?.orderbyid),
          )
        : null;

      const orderByName =
        form?.orderby_name ||
        (orderByContact
          ? `${orderByContact.customercontactfirstname || ""} ${
              orderByContact.customercontactlastname || ""
            }`.trim()
          : "");

      const orderByMobile =
        form?.orderby_mobile || orderByContact?.customercontactmobile || "";

      if (orderByName) {
        result.orderBy = buildTripsheetMessage({
          templateType: "TRIPSHEET_ORDERER",

          name: orderByName,

          mobile: orderByMobile,

          values: [
            clientName, // {{1}} Client Name
            orderByName, // {{2}} Order By Name
            tripsheetNumber, // {{3}} Tripsheet Number
            customerNameForMessage, // {{4}} Customer Name
            vehicleType, // {{5}} Vehicle Type
            vehicleNo, // {{6}} Vehicle Number
            driverName, // {{7}} Driver Name
            driverMobile, // {{8}} Driver Mobile
            reportDate, // {{9}} Report Date
            reportTime, // {{10}} Report Time
            firstUserPickup, // {{11}} Pick-up Point
            firstUserDrop, // {{12}} Drop Point
            helpline, // {{13}} Helpline
          ],
        });
      }
    }

    // ============================================================
    // DRIVER
    //
    // TRIPSHEET_DRIVER
    //
    // {{1}} Client Name
    // {{2}} Driver Name
    // {{3}} Tripsheet Number
    // {{4}} Customer Name
    // {{5}} Vehicle Type
    // {{6}} Vehicle Number
    // {{7}} Report Date
    // {{8}} Report Time
    // {{9}} Pick-up Point
    // {{10}} Drop Point
    // {{11}} Guest / User Name
    // {{12}} Guest / User Mobile
    // {{13}} Helpline
    // ============================================================
    if (tripsheetSettings?.send_message_to_driver === true && driverMobile) {
      result.driver = buildTripsheetMessage({
        templateType: "TRIPSHEET_DRIVER",

        name: driverName,

        mobile: driverMobile,

        values: [
          clientName, // {{1}} Client Name
          driverName, // {{2}} Driver Name
          tripsheetNumber, // {{3}} Tripsheet Number
          customerNameForMessage, // {{4}} Customer Name
          vehicleType, // {{5}} Vehicle Type
          vehicleNo, // {{6}} Vehicle Number
          reportDate, // {{7}} Report Date
          reportTime, // {{8}} Report Time
          firstUserPickup, // {{9}} Pick-up Point
          firstUserDrop, // {{10}} Drop Point
          firstUserName, // {{11}} Guest / User Name
          firstUserMobile, // {{12}} Guest / User Mobile
          helpline, // {{13}} Helpline
        ],
      });
    }

    // ============================================================
    // VENDOR
    //
    // TRIPSHEET_VENDOR
    //
    // {{1}} Client Name
    // {{2}} Vendor Name
    // {{3}} Tripsheet Number
    // {{4}} User Name
    // {{5}} Vehicle Number
    // {{6}} Driver Name
    // {{7}} Driver Mobile
    // {{8}} Report Date
    // {{9}} Report Time
    // {{10}} Pick-up Point
    // {{11}} Drop Point
    // {{12}} Helpline
    // ============================================================
    if (tripsheetSettings?.send_message_to_vendor === true && duty.vendorid) {
      const vendor = dropdownData?.vendors?.find(
        (v) => String(v.vendorid) === String(duty.vendorid),
      );

      const finalVendorName = vendorName || vendor?.vendorname || "";

      const vendorMobile =
        duty.vendor_mobile || vendor?.phone || vendor?.mobile || "";

      result.vendor = buildTripsheetMessage({
        templateType: "TRIPSHEET_VENDOR",

        name: finalVendorName,

        mobile: vendorMobile,

        values: [
          clientName, // {{1}} Client Name
          finalVendorName, // {{2}} Vendor Name
          tripsheetNumber, // {{3}} Tripsheet Number
          firstUserName, // {{4}} User Name
          vehicleNo, // {{5}} Vehicle Number
          driverName, // {{6}} Driver Name
          driverMobile, // {{7}} Driver Mobile
          reportDate, // {{8}} Report Date
          reportTime, // {{9}} Report Time
          firstUserPickup, // {{10}} Pick-up Point
          firstUserDrop, // {{11}} Drop Point
          helpline, // {{12}} Helpline
        ],
      });
    }

    return result;
  };

  const handleCancel = () => {
    console.log("🚫 CANCEL CLICKED");
    console.log("fromBooking:", fromBooking);

    if (fromBooking === true) {
      navigate("/bookings", { replace: true });
      return;
    }

    // Direct Tripsheet edit
    if (onCancel) {
      console.log("➡️ Calling parent onCancel()");
      onCancel();
      return;
    }

    // Fallback only
    window.location.href = "/tripsheets";
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onOrderByNameChange = (event, selectedContact) => {
    if (!selectedContact) {
      setForm((prev) => ({
        ...prev,
        orderby_name: "",
        orderby_mobile: "",
        orderby_email: "",
        orderby_title: "",
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      orderby_name:
        `${selectedContact.customercontactfirstname} ${selectedContact.customercontactlastname}`.trim(),
      orderby_mobile: selectedContact.customercontactmobile || "",
      orderby_email: selectedContact.customercontactemail || "",
      orderby_title: selectedContact.customercontactsalutation || "",
    }));
  };

  const normalizeToArray = (val) => {
    if (Array.isArray(val)) return val;
    if (!val) return [];
    if (typeof val === "object") {
      try {
        return Object.values(val).filter((v) => v !== null && v !== undefined);
      } catch {
        return [];
      }
    }
    return [];
  };

  // Debounced setCharges to reduce rapid updates
  const handleSetCharges = useCallback(
    debounce((updatedCharges) => {
      setDutyForm((prev) => ({
        ...prev,
        charges: {
          ...prev.charges,
          ...updatedCharges,
        },
      }));
    }, 300),
    [],
  );

  // Debounced setPayouts to reduce rapid updates
  const handleSetPayouts = useCallback(
    debounce((updatedPayouts) => {
      setDutyForm((prev) => ({
        ...prev,
        payouts: { ...prev.payouts, ...updatedPayouts },
      }));
    }, 300),
    [],
  );

  const handleDutyFormChange = (field, value) => {
    setDutyForm((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "open_date" || field === "close_date") {
        const openDate = field === "open_date" ? value : prev.open_date;
        const closeDate = field === "close_date" ? value : prev.close_date;
        setDutyDateError(validateDutyDates(openDate, closeDate));
      }

      if (field === "duty") setPayoutsDutyType(value);
      if (
        field === "vehicle_group_id" ||
        field === "assigned_vehicle_group_id"
      ) {
        setPayoutsVehicleGroupId(value);
      }
      return updated;
    });

    if (field === "charges") handleSetCharges(value);
    if (field === "payouts") handleSetPayouts(value);
  };

  // ============================================================
  // SYNC CHARGES → PAYOUTS
  // Parking, Others & Permit
  // Only when Tripsheet Setting is enabled
  // ============================================================
  // ============================================================
  // SYNC CHARGES → PAYOUTS
  // Only fill Payout when existing Payout value is ZERO
  // ============================================================
  useEffect(() => {
    if (!tripsheetSettings.parking_others_permit_to_vehicle) {
      return;
    }

    const charges = dutyForm?.charges || {};
    const payouts = dutyForm?.payouts || {};

    const parking = charges.trip_charges_parking ?? "0.00";
    const others = charges.trip_charges_others ?? "0.00";
    const permit = charges.trip_charges_permit ?? "0.00";

    const isZero = (value) =>
      value === undefined ||
      value === null ||
      value === "" ||
      parseFloat(value) === 0;

    const updatedPayouts = {
      ...payouts,
    };

    let changed = false;

    // Parking
    if (isZero(payouts.trip_payouts_parking)) {
      updatedPayouts.trip_payouts_parking = parking;
      changed = true;
    }

    // Others
    if (isZero(payouts.trip_payouts_others)) {
      updatedPayouts.trip_payouts_others = others;
      changed = true;
    }

    // Permit
    if (isZero(payouts.trip_payouts_permit)) {
      updatedPayouts.trip_payouts_permit = permit;
      changed = true;
    }

    if (!changed) {
      return;
    }

    setDutyForm((prev) => ({
      ...prev,
      payouts: updatedPayouts,
    }));
  }, [
    tripsheetSettings.parking_others_permit_to_vehicle,
    dutyForm?.charges?.trip_charges_parking,
    dutyForm?.charges?.trip_charges_others,
    dutyForm?.charges?.trip_charges_permit,
  ]);

  // ============================================================
  // BUILD THE CURRENT dutyForm INTO A FINAL, SAVE-READY DUTY OBJECT
  // Shared by saveDuty() (Update Duty button) and getEffectiveTripDuties()
  // (main Save/Update button), so both paths produce identical results.
  // ============================================================
  const buildDutyToSave = () => {
    const fullCharges =
      chargesRef.current?.getValues?.() || dutyForm.charges || {};

    const fullPayouts =
      payoutsRef.current?.getValues?.() || dutyForm.payouts || {};

    const fullPayoutsWithFields = {
      ...fullPayouts,
      trip_payouts_duty:
        payoutsDutyType ||
        fullPayouts.trip_payouts_duty ||
        dutyForm.payouts?.trip_payouts_duty ||
        dutyForm?.trip_payouts_duty ||
        dutyForm.duty ||
        "",
      trip_payouts_vehicle_group_id:
        payoutsVehicleGroupId ??
        fullPayouts.trip_payouts_vehicle_group_id ??
        dutyForm.payouts?.trip_payouts_vehicle_group_id ??
        dutyForm?.trip_payouts_vehicle_group_id ??
        dutyForm.assigned_vehicle_group_id ??
        null,
    };

    return {
      ...dutyForm,
      duty_date: convertDate(dutyForm.duty_date),
      charges: { ...fullCharges },
      payouts: { ...fullPayoutsWithFields },
      trip_payouts_duty: fullPayoutsWithFields.trip_payouts_duty,
      trip_payouts_vehicle_group_id:
        fullPayoutsWithFields.trip_payouts_vehicle_group_id,
    };
  };

  // ============================================================
  // COMMIT ANY PENDING (UNSAVED) DUTY EDIT INTO THE TRIP_DUTIES LIST
  //
  // Fixes the bug where a user edits a duty (or starts a new one),
  // changes charges/payouts/dates, but clicks the main Save/Update
  // button instead of "Update Duty" / "+Add Duty" — previously those
  // changes were silently discarded.
  //
  // Returns the up-to-date trip_duties array synchronously, since
  // setForm() is async and can't be relied on later in the same
  // function call.
  // ============================================================
  const getEffectiveTripDuties = () => {
    const existingDuties = Array.isArray(form.trip_duties)
      ? [...form.trip_duties]
      : [];

    // Nothing is being actively edited or drafted — nothing to merge
    if (!dutyForm || !dutyForm.duty) {
      return existingDuties;
    }

    const dutyToSave = buildDutyToSave();

    if (dutyEditIndex >= 0 && existingDuties[dutyEditIndex]) {
      // User was editing an existing duty — commit the latest changes
      existingDuties[dutyEditIndex] = dutyToSave;
    } else if (dutyEditIndex === -1) {
      // A brand-new duty was filled in but never explicitly added.
      // Respect the SINGLE duty entry limit.
      if (!(dutyEntryType === "SINGLE" && existingDuties.length >= 1)) {
        existingDuties.push(dutyToSave);
      }
    }

    return existingDuties;
  };

  const saveDuty = () => {
    if (!dutyForm || !dutyForm.duty) {
      setSnackbar({
        open: true,
        message: "Duty type is required",
        severity: "error",
      });
      return;
    }

    const existingDuties = Array.isArray(form.trip_duties)
      ? form.trip_duties
      : [];

    if (
      dutyEntryType === "SINGLE" &&
      dutyEditIndex === -1 &&
      existingDuties.length >= 1
    ) {
      setSnackbar({
        open: true,
        message: "Only one duty is allowed for this Tripsheet.",
        severity: "error",
      });
      return;
    }

    const dutyToSave = buildDutyToSave();

    setForm((prev) => {
      const duties = Array.isArray(prev.trip_duties)
        ? [...prev.trip_duties]
        : [];

      if (dutyEditIndex >= 0 && duties[dutyEditIndex]) {
        duties[dutyEditIndex] = dutyToSave;
      } else {
        duties.push(dutyToSave);
      }

      return { ...prev, trip_duties: duties };
    });

    setDutyForm(initialDuty);
    setDutyEditIndex(-1);
    setSelectedDutyType("");
    setSelectedVehicleGroupId("");
    setShowChargesAndPayouts(false);

    setSnackbar({ open: true, message: "Duty saved", severity: "success" });
  };

  const handleEditDuty = (index) => {
    const dutyToEdit = form.trip_duties?.[index];

    if (!dutyToEdit) {
      console.warn("⚠️ Duty not found:", index);
      return;
    }

    console.log("🔹 Editing duty:", dutyToEdit);

    // ------------------------------------------------------------
    // Normalize date
    // Keep ISO format because MyDatePicker converts it to Date.
    // ------------------------------------------------------------
    const normalizeDate = (value) => {
      if (!value) return null;

      if (value instanceof Date) {
        return value.toISOString();
      }

      if (typeof value === "string") {
        // Already ISO / timestamp
        if (value.includes("T")) {
          return value;
        }

        // YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return `${value}T00:00:00.000Z`;
        }
      }

      return value;
    };

    // ------------------------------------------------------------
    // Normalize time
    // PostgreSQL returns HH:mm:ss
    // HTML time input requires HH:mm
    // ------------------------------------------------------------
    const normalizeTime = (value) => {
      if (!value) return "";

      if (typeof value === "string") {
        return value.length >= 5 ? value.substring(0, 5) : value;
      }

      return "";
    };

    // ------------------------------------------------------------
    // Normalize KM
    // ------------------------------------------------------------
    const normalizeKm = (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      return String(value);
    };

    const normalizedDuty = {
      ...dutyToEdit,

      // IDs
      vehicleid:
        dutyToEdit.vehicleid !== null && dutyToEdit.vehicleid !== undefined
          ? Number(dutyToEdit.vehicleid)
          : null,

      assigned_vehicle_group_id:
        dutyToEdit.assigned_vehicle_group_id !== null &&
        dutyToEdit.assigned_vehicle_group_id !== undefined
          ? Number(dutyToEdit.assigned_vehicle_group_id)
          : null,

      dispatched_vehicle_group_id:
        dutyToEdit.dispatched_vehicle_group_id !== null &&
        dutyToEdit.dispatched_vehicle_group_id !== undefined
          ? Number(dutyToEdit.dispatched_vehicle_group_id)
          : null,

      driver_id:
        dutyToEdit.driver_id !== null && dutyToEdit.driver_id !== undefined
          ? Number(dutyToEdit.driver_id)
          : null,

      // ----------------------------------------------------------
      // Dates
      // ----------------------------------------------------------
      open_date: normalizeDate(dutyToEdit.open_date),
      close_date: normalizeDate(dutyToEdit.close_date),

      // ----------------------------------------------------------
      // Times
      // ----------------------------------------------------------
      open_time: normalizeTime(dutyToEdit.open_time),
      reporting_time: normalizeTime(dutyToEdit.reporting_time),
      close_time: normalizeTime(dutyToEdit.close_time),

      // ----------------------------------------------------------
      // KM
      // ----------------------------------------------------------
      open_km_reading: normalizeKm(dutyToEdit.open_km_reading),
      close_km_reading: normalizeKm(dutyToEdit.close_km_reading),

      // ----------------------------------------------------------
      // Nested data
      // ----------------------------------------------------------
      charges: dutyToEdit.charges || {},
      payouts: dutyToEdit.payouts || {},

      trip_passengers: Array.isArray(dutyToEdit.trip_passengers)
        ? dutyToEdit.trip_passengers
        : [],

      trip_itineraries: Array.isArray(dutyToEdit.trip_itineraries)
        ? dutyToEdit.trip_itineraries
        : [],
    };

    console.log("✅ Normalized duty:", normalizedDuty);

    setDutyForm(normalizedDuty);
    setDutyEditIndex(index);

    setSelectedDutyType(normalizedDuty.duty || "");

    setSelectedVehicleGroupId(normalizedDuty.assigned_vehicle_group_id || "");

    setPayoutsDutyType(
      normalizedDuty.payouts?.trip_payouts_duty || normalizedDuty.duty || "",
    );

    setPayoutsVehicleGroupId(
      normalizedDuty.payouts?.trip_payouts_vehicle_group_id ||
        normalizedDuty.assigned_vehicle_group_id ||
        "",
    );

    // Update payout child
    if (payoutsRef.current) {
      payoutsRef.current.setLocalDutyType(
        normalizedDuty.payouts?.trip_payouts_duty || normalizedDuty.duty || "",
      );

      payoutsRef.current.setLocalVehicleGroupId(
        normalizedDuty.payouts?.trip_payouts_vehicle_group_id ||
          normalizedDuty.assigned_vehicle_group_id ||
          "",
      );
    }

    // Update charges/payouts
    handleSetCharges(normalizedDuty.charges);
    handleSetPayouts(normalizedDuty.payouts);
  };

  useEffect(() => {
    if (dutyEditIndex >= 0 && dutyForm) {
      setPayoutsDutyType(dutyForm.duty || "");
      setPayoutsVehicleGroupId(dutyForm.assigned_vehicle_group_id || "");
      setSelectedDutyType(dutyForm.duty || "");
      setSelectedVehicleGroupId(dutyForm.assigned_vehicle_group_id || "");

      // setResetChargesSignal((prev) => prev + 1);
      // setResetPayoutsSignal((prev) => prev + 1);
    }
  }, [dutyForm?.duty, dutyForm?.assigned_vehicle_group_id, dutyEditIndex]);

  const handleDeleteDuty = (idx) => {
    setForm((prev) => ({
      ...prev,
      trip_duties: prev.trip_duties.filter((_, i) => i !== idx),
    }));

    if (dutyEditIndex === idx) {
      setDutyEditIndex(-1);
      setDutyForm(initialDuty);
    }
  };

  function handleAddPassenger(newPassenger) {
    console.log("Adding passenger:", newPassenger);
    setPassengers((oldPassengers) => [...oldPassengers, newPassenger]);
  }

  function handleEditPassenger(index, updatedPassenger) {
    console.log("Editing passenger at index", index, ":", updatedPassenger);
    setPassengers((oldPassengers) => {
      const copy = [...oldPassengers];
      // Always preserve original id
      copy[index] = { ...updatedPassenger, id: oldPassengers[index].id };
      return copy;
    });
  }

  function handleDeletePassenger(index) {
    setPassengers((oldPassengers) =>
      oldPassengers.filter((_, i) => i !== index),
    );
  }

  const handleAddItinerary = (newItinerary) => {
    setItineraries((old) => [...old, newItinerary]);
  };

  const handleEditItinerary = (index, updatedItinerary) => {
    setItineraries((old) => {
      const copy = [...old];
      copy[index] = updatedItinerary;
      return copy;
    });
  };

  const handleDeleteItinerary = (index) => {
    setItineraries((old) => old.filter((_, i) => i !== index));
  };

  const normalizedDuties = (form.trip_duties || []).map((duty) => {
    return {
      ...duty,
      fyid: duty.fyid || form.fyid || 1,
      open_km_reading:
        duty.open_km_reading && duty.open_km_reading !== ""
          ? Number(duty.open_km_reading)
          : 0,
      close_km_reading:
        duty.close_km_reading && duty.close_km_reading !== ""
          ? Number(duty.close_km_reading)
          : 0,
      charges: duty.charges || {},
      payouts: duty.payouts || {},
      attachments: Array.isArray(duty.attachments) ? duty.attachments : [],
      trip_itineraries: Array.isArray(duty.trip_itineraries)
        ? duty.trip_itineraries
        : [],
      trip_passengers: Array.isArray(duty.trip_passengers)
        ? duty.trip_passengers
        : [],
    };
  });

  // ============================================================
  // CHECK WHETHER TRIPSHEET IS IN OPEN / INITIAL STAGE
  //
  // Preview should automatically open when:
  //   Open Date     = available
  //   Open Time     = available
  //   Opening KM    = available
  //
  // AND closing details are not entered:
  //
  //   Close Date    = null / empty
  //   Close Time    = null / empty
  //   Closing KM    = null / empty / 0
  //
  // This is intentionally checked from trip_duties.
  // ============================================================
  const shouldAutoPreviewTripsheet = (tripDuties) => {
    if (!Array.isArray(tripDuties) || tripDuties.length === 0) {
      return false;
    }

    return tripDuties.some((duty) => {
      const hasOpenDate =
        duty?.open_date !== null &&
        duty?.open_date !== undefined &&
        String(duty.open_date).trim() !== "";

      const hasOpenTime =
        (duty?.open_time !== null &&
          duty?.open_time !== undefined &&
          String(duty.open_time).trim() !== "") ||
        (duty?.reporting_time !== null &&
          duty?.reporting_time !== undefined &&
          String(duty.reporting_time).trim() !== "");

      const hasOpenKm =
        duty?.open_km_reading !== null &&
        duty?.open_km_reading !== undefined &&
        String(duty.open_km_reading).trim() !== "";

      const hasCloseDate =
        duty?.close_date !== null &&
        duty?.close_date !== undefined &&
        String(duty.close_date).trim() !== "";

      const hasCloseTime =
        duty?.close_time !== null &&
        duty?.close_time !== undefined &&
        String(duty.close_time).trim() !== "";

      const hasCloseKm =
        duty?.close_km_reading !== null &&
        duty?.close_km_reading !== undefined &&
        String(duty.close_km_reading).trim() !== "" &&
        Number(duty.close_km_reading) !== 0;

      return hasOpenDate && hasOpenTime && !hasCloseTime && !hasCloseKm;
    });
  };

  const openTripsheetMessageDialog = ({ duties = [], passengers = [] }) => {
    const duty = duties[0] || {};

    const users = (passengers || [])
      .filter((p) => p?.used_by_name)
      .slice(0, 3)
      .map((p) => ({
        name: `${p.used_by_title || ""} ${p.used_by_name || ""}`.trim(),
        mobile: p.used_by_mobile || "",
        message: `Dear ${`${p.used_by_title || ""} ${p.used_by_name || ""}`.trim()},

Your Tripsheet ${form.tripsheet_number || ""} has been created.

Thank you.`,
      }));

    const driver =
      duty.driver_name || duty.driver_mobile
        ? {
            name: duty.driver_name || "",
            mobile: duty.driver_mobile || "",
            message: `Dear ${duty.driver_name || "Driver"},

Your Tripsheet ${form.tripsheet_number || ""} has been created.

Thank you.`,
          }
        : null;

    const vendor = duty.is_vendor_tripsheet
      ? {
          name: duty.vendor_vehicle_no
            ? `Vendor - ${duty.vendor_vehicle_no}`
            : "Vendor",
          mobile: duty.vendor_mobile || "",
          message: `Dear Vendor,

Your Tripsheet ${form.tripsheet_number || ""} has been created.

Thank you.`,
        }
      : null;

    setMessageRecipients({
      users,
      driver,
      vendor,
    });

    setMessageDialogOpen(true);
  };

  const finishAfterMessaging = () => {
    setMessageDialogOpen(false);

    if (fromBooking === true) {
      navigate("/bookings", {
        replace: true,
      });
      return;
    }

    if (typeof onSuccess === "function") {
      onSuccess();
      return;
    }

    window.location.href = "/tripsheets";
  };

  // ============================================================
  // TRIPSHEET MESSAGING — per-recipient helpers
  // ============================================================
  const updateRecipient = (group, index, field, value) => {
    setMessageRecipients((prev) => ({
      ...prev,
      [group]: prev[group].map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const updateSingleRecipient = (group, field, value) => {
    setMessageRecipients((prev) => ({
      ...prev,
      [group]: prev[group] ? { ...prev[group], [field]: value } : prev[group],
    }));
  };

  const getAllRecipientEntries = () => {
    const entries = [];
    (messageRecipients?.users || []).forEach((u, i) =>
      entries.push({ group: "users", index: i, ...u }),
    );
    if (messageRecipients?.orderBy)
      entries.push({ group: "orderBy", ...messageRecipients.orderBy });
    if (messageRecipients?.driver)
      entries.push({ group: "driver", ...messageRecipients.driver });
    if (messageRecipients?.vendor)
      entries.push({ group: "vendor", ...messageRecipients.vendor });
    return entries;
  };

  const allRecipientEntries = getAllRecipientEntries();
  const totalRecipients = allRecipientEntries.length;
  const includedEntries = allRecipientEntries.filter(
    (e) => e.included !== false,
  );
  const readyToSendCount = includedEntries.filter(
    (e) =>
      e.mobile &&
      String(e.mobile).trim() &&
      e.message &&
      String(e.message).trim(),
  ).length;
  const allIncluded =
    totalRecipients > 0 && includedEntries.length === totalRecipients;
  const someIncluded = includedEntries.length > 0;

  const toggleAllRecipients = (checked) => {
    setMessageRecipients((prev) => {
      const next = { ...prev };
      if (Array.isArray(next.users)) {
        next.users = next.users.map((u) => ({ ...u, included: checked }));
      }
      if (next.orderBy) next.orderBy = { ...next.orderBy, included: checked };
      if (next.driver) next.driver = { ...next.driver, included: checked };
      if (next.vendor) next.vendor = { ...next.vendor, included: checked };
      return next;
    });
  };

  const handleSendMessages = () => {
    const toSend = includedEntries.filter(
      (e) =>
        e.mobile &&
        String(e.mobile).trim() &&
        e.message &&
        String(e.message).trim(),
    );

    console.log("Tripsheet messages to send:", toSend);
    // TODO: wire this up to the actual WhatsApp/SMS sending API

    finishAfterMessaging();
  };

  const handleSave = async () => {
    if (!clientId) {
      setSnackbar({
        open: true,
        message: "Client ID missing",
        severity: "error",
      });
      return;
    }

        if (!form) {
      setSnackbar({
        open: true,
        message: "Form data missing",
        severity: "error",
      });
      return;
    }

    if (!form.fyid && !fyid) {
      setSnackbar({
        open: true,
        message: "Financial year not selected",
        severity: "error",
      });
      return;
    }

    let tripsheetNumber = form.tripsheet_number;

    // ============================================================
    // GENERATE TRIPSHEET NUMBER FOR NEW TRIPSHEET
    // ============================================================
    if (!form.tripsheetid) {
      try {
        const createdDoc = await createDocument(
          clientId,
          "Tripsheet",
          "Tripsheet Document",
        );

        tripsheetNumber = createdDoc.document_number;
      } catch (err) {
        console.error("❌ Failed to generate tripsheet number:", err);

        setSnackbar({
          open: true,
          message: "Failed to generate tripsheet number",
          severity: "error",
        });

        return;
      }
    }

    // ============================================================
    // NORMALIZE PASSENGERS
    // ============================================================
    const normalizedPassengers = (passengers || []).map((p) => ({
      id: p.id ?? p.dbId,

      used_by_id:
        p.used_by_id != null && p.used_by_id !== ""
          ? p.used_by_id
          : p.used_contact?.contactid ||
            p.used_contact?.customercontactid ||
            null,

      used_by_title:
        p.used_by_title != null && p.used_by_title !== ""
          ? p.used_by_title
          : p.used_contact?.customercontactsalutation || p.used_title || "",

      used_by_name: p.used_name || "",
      used_by_mobile: p.used_mobile || "",
      used_by_email: p.used_email || "",
      used_by_department: p.used_department || "",

      report_address: p.report_address || "",
      drop_address: p.drop_address || "",
    }));

    // ============================================================
    // COMMIT ANY PENDING (UNSAVED) DUTY EDIT
    //
    // getEffectiveTripDuties() rebuilds the currently edited/drafted
    // duty and merges it into form.trip_duties.
    // ============================================================
    const effectiveTripDuties = getEffectiveTripDuties();

    // ============================================================
    // NORMALIZE DUTIES
    // ============================================================
    const normalizedDuties = (effectiveTripDuties || []).map((duty) => {
      return {
               ...duty,

        fyid: duty.fyid ?? form.fyid ?? Number(fyid),

        open_date: convertDate(duty.open_date),
            close_date: convertDate(duty.close_date),

        charges: {
          ...duty.charges,

          trip_charges_duty: duty.charges?.trip_charges_duty || duty.duty || "",

          trip_charges_vehicle_group_id:
            duty.charges?.trip_charges_vehicle_group_id ||
            duty.assigned_vehicle_group_id ||
            "",

          trip_charges_extra_kms:
            Math.round(Number(duty.charges?.trip_charges_extra_kms)) || 0,

          trip_charges_kms_used:
            Math.round(Number(duty.charges?.trip_charges_kms_used)) || 0,

          trip_charges_days_used:
            Math.round(Number(duty.charges?.trip_charges_days_used)) || 0,

          // ==========================================================
          // MULTIPLE OTHERS
          // ==========================================================
          trip_charges_others: duty.charges?.trip_charges_others ?? "0.00",

          others_entries: Array.isArray(duty.charges?.others_entries)
            ? duty.charges.others_entries
            : [],

          // ==========================================================
          // MULTIPLE PERMIT
          // ==========================================================
          trip_charges_permit: duty.charges?.trip_charges_permit ?? "0.00",

          permit_entries: Array.isArray(duty.charges?.permit_entries)
            ? duty.charges.permit_entries
            : [],
        },

        payouts: {
          ...duty.payouts,

          trip_payouts_duty: duty.payouts?.trip_payouts_duty || duty.duty || "",

          trip_payouts_vehicle_group_id:
            duty.payouts?.trip_payouts_vehicle_group_id ||
            duty.assigned_vehicle_group_id ||
            "",

          trip_payouts_extra_kms:
            Math.round(Number(duty.payouts?.trip_payouts_extra_kms)) || 0,

          trip_payouts_kms_used:
            Math.round(Number(duty.payouts?.trip_payouts_kms_used)) || 0,

          trip_payouts_days_used:
            Math.round(Number(duty.payouts?.trip_payouts_days_used)) || 0,
        },
      };
    });

    // ============================================================
    // PREPARE PAYLOAD
    // ============================================================
    const payload = {
      ...form,

      tripsheet_number: tripsheetNumber,

            client_id: clientId,

      fyid: form.fyid ?? Number(fyid),

      orderbyid: form.orderbyid || null,

      passengers: normalizedPassengers,

      itineraries: Array.isArray(itineraries) ? itineraries : [],

      trip_duties: normalizedDuties,
    };

    // ============================================================
    // SAVE / UPDATE
    // ============================================================
    try {
      let savedTripsheetId = form.tripsheetid || null;

      // ==========================================================
      // UPDATE EXISTING TRIPSHEET
      // ==========================================================
      if (form.tripsheetid) {
        await axiosInstance.put(`/api/tripsheets/${form.tripsheetid}`, payload);

        savedTripsheetId = form.tripsheetid;

        setSnackbar({
          open: true,
          message: "Tripsheet updated",
          severity: "success",
        });
      }

      // ==========================================================
      // CREATE NEW TRIPSHEET
      // ==========================================================
      else {
        const saveResponse = await axiosInstance.post(
          "/api/tripsheets",
          payload,
        );

        // Backend currently returns:
        // { tripsheetId }
        savedTripsheetId =
          saveResponse?.data?.tripsheetId ||
          saveResponse?.data?.tripsheetid ||
          saveResponse?.data?.id ||
          null;

        setSnackbar({
          open: true,
          message: "Tripsheet created",
          severity: "success",
        });
      }

      // ============================================================
      // COMMIT MERGED DUTIES INTO FORM STATE
      // ============================================================
      setForm((prev) => ({
        ...prev,
        tripsheetid: savedTripsheetId || prev.tripsheetid,
        tripsheet_number: tripsheetNumber,
        trip_duties: effectiveTripDuties,
      }));

      // ============================================================
      // CLEAR DUTY FORM
      // ============================================================
      setDutyForm(initialDuty);
      setDutyEditIndex(-1);
      setSelectedDutyType("");
      setSelectedVehicleGroupId("");

      // ============================================================
      // AUTOMATIC TRIPSHEET PREVIEW
      //
      // IMPORTANT:
      // There is NO Print button.
      //
      // If Tripsheet Settings has enable_print = true,
      // the next step will generate the selected Tripsheet
      // template and automatically open PreviewModal.
      // ============================================================

      const shouldMessage =
        (tripsheetSettings?.send_message_to_users === true ||
          tripsheetSettings?.send_message_to_driver === true ||
          tripsheetSettings?.send_message_to_vendor === true) &&
        shouldAutoPreviewTripsheet(normalizedDuties);

      let messageClientInfo = clientInfo || null;

      if (shouldMessage && clientId) {
        try {
          const clientResponse = await axiosInstance.get(
            `/api/clients/${clientId}`,
          );

          messageClientInfo =
            clientResponse?.data?.data ||
            clientResponse?.data ||
            messageClientInfo;
        } catch (error) {
          console.error(
            "Failed to load client information for Tripsheet messaging:",
            error,
          );
        }
      }

      const messageData = shouldMessage
        ? prepareTripsheetMessages(normalizedDuties, messageClientInfo)
        : null;

      const shouldPreview =
        tripsheetSettings?.enable_print === true &&
        shouldAutoPreviewTripsheet(normalizedDuties) &&
        !autoPreviewShownRef.current;

      if (shouldPreview) {
        if (messageData) {
          pendingMessageRecipientsRef.current = messageData;
        }
        console.log("🖨️ Opening Tripsheet preview", {
          tripsheetId: savedTripsheetId,
          tripsheetNumber,
          templateId: tripsheetSettings?.template_id,
        });

        autoPreviewShownRef.current = true;

        const previewPayload = {
          tripsheet: {
            ...form,
            tripsheetid: savedTripsheetId,
            tripsheet_number: tripsheetNumber,
            client_id: clientId,
          },

          trip_duties: normalizedDuties,

          passengers: normalizedPassengers,

          itineraries: Array.isArray(itineraries) ? itineraries : [],

          template_id: tripsheetSettings?.template_id || null,

          print_client_name_address_vendor_vehicle:
            tripsheetSettings?.print_client_name_address_vendor_vehicle ===
            true,
        };

        setPreviewData(previewPayload);

        try {
          const previewResponse = await axiosInstance.post(
            "/api/document-template/tripsheet/preview",
            previewPayload,
          );

          const html = previewResponse?.data?.html || "";

          if (!html) {
            throw new Error("Tripsheet preview HTML was not generated");
          }

          setPreviewHtml(html);
          setPreviewOpen(true);

          console.log("✅ Tripsheet preview opened");
        } catch (previewError) {
          console.error(
            "❌ Tripsheet preview generation failed:",
            previewError,
          );

          setSnackbar({
            open: true,
            message:
              "Tripsheet saved, but preview could not be generated: " +
              (previewError.response?.data?.error ||
                previewError.response?.data?.message ||
                previewError.message ||
                "Unknown error"),
            severity: "error",
          });

          // Keep existing navigation behaviour if preview fails
          if (fromBooking === true) {
            navigate("/bookings", {
              replace: true,
            });
          } else if (typeof onSuccess === "function") {
            onSuccess();
          } else {
            window.location.href = "/tripsheets";
          }
        }

        return;
      }

      // ============================================================
      // NORMAL EXISTING BEHAVIOUR WHEN PRINT IS DISABLED
      // ============================================================
      else {
        if (shouldMessage && messageData) {
          setMessageRecipients(messageData);
          setMessageDialogOpen(true);
          return;
        }

        if (fromBooking === true) {
          // ========================================================
          // BOOKING → TRIPSHEET
          // ========================================================
          navigate("/bookings", {
            replace: true,
          });
        } else {
          // ========================================================
          // DIRECT TRIPSHEET → EDIT
          // ========================================================
          if (typeof onSuccess === "function") {
            onSuccess();
          } else {
            console.warn("⚠️ onSuccess callback is not available.");

            window.location.href = "/tripsheets";
          }
        }
      }
       } catch (error) {
      const responseData = error.response?.data;

      console.error(
        "❌ Failed to save tripsheet:",
        responseData || error.message
      );

      const message =
        responseData?.message ||
        responseData?.error ||
        error.message ||
        "Failed to save tripsheet.";

      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        height: "calc(100vh - 100px)",
        p: 1,
        bgcolor: "#f8fafc",
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <TripsheetHeader
        form={form}
        handleFormChange={handleFormChange}
        dropdownData={dropdownData}
        clientId={clientId}
        onOrderByChange={onOrderByNameChange}
        onCustomerChange={setCustomerName}
      />

      {(dutyEntryType === "MULTIPLE" ||
        form.trip_duties.length === 0 ||
        dutyEditIndex >= 0) && (
        <DutyForm
          dutyForm={dutyForm}
          handleDutyFormChange={handleDutyFormChange}
          vehicles={dropdownData.vehicles}
          vendors={dropdownData.vendors}
          drivers={dropdownData.drivers}
          vehicleGroups={dropdownData.vehicleGroups}
          locations={dropdownData.locations}
          onSaveDuty={saveDuty}
          dutyEditIndex={dutyEditIndex}
          setDutyEditIndex={setDutyEditIndex}
          setDutyForm={setDutyForm}
          clientId={clientId}
          selectedDutyType={selectedDutyType}
          setSelectedDutyType={setSelectedDutyType}
          selectedVehicleGroupId={selectedVehicleGroupId}
          setSelectedVehicleGroupId={setSelectedVehicleGroupId}
          dutyDateError={dutyDateError}
          dutyEntryType={dutyEntryType}
          carryForwardOpeningKm={
            tripsheetSettings?.carry_forward_opening_km === true
          }
          checkVehicleDocumentsExpiry={
            tripsheetSettings?.check_vehicle_documents_expiry === true
          }
          checkTripsheetsReturned={tripsheetSettings.check_tripsheets_returned}
        />
      )}
      {Array.isArray(form.trip_duties) && form.trip_duties.length > 0 && (
        <DutyTable
          duties={Array.isArray(form.trip_duties) ? form.trip_duties : []}
          vehicles={
            Array.isArray(dropdownData.vehicles) ? dropdownData.vehicles : []
          }
          vehicleGroups={
            Array.isArray(dropdownData.vehicleGroups)
              ? dropdownData.vehicleGroups
              : []
          }
          vendors={
            Array.isArray(dropdownData.vendors) ? dropdownData.vendors : []
          }
          editDuty={handleEditDuty}
          deleteDuty={handleDeleteDuty}
          clientId={clientId}
          tripsheetId={form.tripsheetid || tripsheetId}
          setDuties={(updater) =>
            setForm((prev) => ({
              ...prev,
              trip_duties:
                typeof updater === "function"
                  ? updater(prev.trip_duties || [])
                  : updater,
            }))
          }
        />
      )}
      {dutyForm?.close_km_reading &&
        dutyForm?.close_time &&
        dutyForm?.close_date && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(625px, 100%), 1fr))",
              gap: "8px",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              paddingLeft: "0px",
              paddingTop: "0px",
              marginTop: "20px",
            }}
          >
            {/* <div style={{ flex: "1 1 45%", minWidth: "300px" }}> */}
            <div
              style={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              <ChargesForm
                ref={chargesRef}
                // key={`charges-${dutyForm?.trip_duty_id || "new"}-${dutyEditIndex || 0}-${resetChargesSignal}`}
                key={`charges-${dutyForm?.trip_duty_id || "new"}-${
                  dutyEditIndex || 0
                }`}
                charges={dutyForm.charges || {}}
                setCharges={handleSetCharges}
                // resetSignal={resetChargesSignal}
                dutyForm={dutyForm}
                customerName={customerName}
                tripChargesDaysUsed={tripChargesDaysUsed}
                tripChargesHoursUsed={tripChargesHoursUsed}
                hoursUsedDisplay={hoursUsedDisplayString}
                tripChargesKmsUsed={tripChargesKmsUsed}
                tariff={tariff}
                tariffSlabs={tariffSlabs}
                usageTimeRoundoff={usageTimeRoundoff}
                selectedDutyType={selectedDutyType}
                customerId={customerId}
                handleDutyFormChange={handleDutyFormChange}
                setTripChargesDaysUsed={setTripChargesDaysUsed}
                setTripChargesKmsUsed={setTripChargesKmsUsed}
                selectedVehicleGroupId={selectedVehicleGroupId}
                setSelectedVehicleGroupId={setSelectedVehicleGroupId}
                vehicleGroups={dropdownData.vehicleGroups}
                multipleOthersEntry={tripsheetSettings.multiple_others_entry}
                multiplePermitEntry={tripsheetSettings.multiple_permit_entry}
                parkingOthersPermitToVehicle={
                  tripsheetSettings.parking_others_permit_to_vehicle
                }
              />
            </div>
            {/* <div style={{ flex: "1 1 45%", minWidth: "300px" }}> */}
            <div
              style={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              <PayoutsForm
                ref={payoutsRef}
                // key={`payouts-${dutyForm?.trip_duty_id || "new"}-${dutyEditIndex || 0}-${resetPayoutsSignal}`}
                key={`payouts-${dutyForm?.trip_duty_id || "new"}-${
                  dutyEditIndex || 0
                }`}
                payouts={dutyForm.payouts || {}}
                setPayouts={handleSetPayouts}
                // resetSignal={resetPayoutsSignal}
                setTripPayoutsDaysUsed={setTripPayoutsDaysUsed}
                setTripPayoutsKmsUsed={setTripPayoutsKmsUsed}
                dutyForm={dutyForm}
                vehicleGroups={dropdownData.vehicleGroups}
                vendorName={vendorName}
                vendorVehicleNo={dutyForm.vendor_vehicle_no}
                vehicleNumber={vehicleNumber}
                tripPayoutsDaysUsed={tripPayoutsDaysUsed}
                tripPayoutsHoursUsed={tripPayoutsHoursUsed}
                hoursUsedDisplay={hoursUsedDisplayString}
                tripPayoutsKmsUsed={tripPayoutsKmsUsed}
                tariff={payoutsTariff}
                tariffSlabs={payoutsTariffSlabs}
                payoutsUsageTimeRoundoff={payoutsUsageTimeRoundoff}
                customerId={customerId}
                handleDutyFormChange={handleDutyFormChange}
                selectedDutyType={payoutsDutyType}
                setSelectedDutyType={setPayoutsDutyType}
                selectedVehicleGroupId={payoutsVehicleGroupId}
                setSelectedVehicleGroupId={setPayoutsVehicleGroupId}
              />
            </div>
          </div>
        )}

      <PassengerSubform
        passengers={passengers}
        onAdd={handleAddPassenger}
        onEdit={handleEditPassenger}
        onDelete={handleDeletePassenger}
        clientId={clientId}
        customerId={form.customerid}
        dropdownData={dropdownData}
      />

      {["Outstation", "OSDayRate", "Package"].includes(dutyForm.duty) && (
        <ItinerarySubform
          itineraries={itineraries}
          onAdd={handleAddItinerary}
          onEdit={handleEditItinerary}
          onDelete={handleDeleteItinerary}
        />
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button variant="contained" color="primary" onClick={handleSave}>
          {editData ? "Update" : "Save"}
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
      </Box>
      <Dialog
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "85vw",
            maxWidth: "1200px",
            maxHeight: "120vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Tripsheet Messaging
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Review and edit messages before sending to recipients
            </Typography>
          </Box>

          {totalRecipients > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={allIncluded}
                  indeterminate={someIncluded && !allIncluded}
                  onChange={(e) => toggleAllRecipients(e.target.checked)}
                />
              }
              label="Select All"
              sx={{ mr: 0 }}
            />
          )}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 2,
            bgcolor: "#fafafa",
            overflowY: "auto",
          }}
        >
          {totalRecipients === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography color="text.secondary">
                No recipients are configured for messaging on this Tripsheet.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              {/* USERS */}
              {messageRecipients?.users?.map((user, index) => (
                <MessageRecipientCard
                  key={`user-${index}`}
                  accentColor="#1976d2"
                  roleLabel={`User ${index + 1}`}
                  name={user?.name}
                  mobile={user?.mobile}
                  message={user?.message}
                  included={user?.included !== false}
                  onToggleIncluded={(checked) =>
                    updateRecipient("users", index, "included", checked)
                  }
                  onMobileChange={(value) =>
                    updateRecipient("users", index, "mobile", value)
                  }
                  onMessageChange={(value) =>
                    updateRecipient("users", index, "message", value)
                  }
                />
              ))}

              {/* ORDER BY */}
              {messageRecipients?.orderBy && (
                <MessageRecipientCard
                  accentColor="#7b1fa2"
                  roleLabel="Order By"
                  name={messageRecipients.orderBy?.name}
                  mobile={messageRecipients.orderBy?.mobile}
                  message={messageRecipients.orderBy?.message}
                  included={messageRecipients.orderBy?.included !== false}
                  onToggleIncluded={(checked) =>
                    updateSingleRecipient("orderBy", "included", checked)
                  }
                  onMobileChange={(value) =>
                    updateSingleRecipient("orderBy", "mobile", value)
                  }
                  onMessageChange={(value) =>
                    updateSingleRecipient("orderBy", "message", value)
                  }
                />
              )}

              {/* DRIVER */}
              {messageRecipients?.driver && (
                <MessageRecipientCard
                  accentColor="#2e7d32"
                  roleLabel="Driver"
                  name={messageRecipients.driver?.name}
                  mobile={messageRecipients.driver?.mobile}
                  message={messageRecipients.driver?.message}
                  included={messageRecipients.driver?.included !== false}
                  onToggleIncluded={(checked) =>
                    updateSingleRecipient("driver", "included", checked)
                  }
                  onMobileChange={(value) =>
                    updateSingleRecipient("driver", "mobile", value)
                  }
                  onMessageChange={(value) =>
                    updateSingleRecipient("driver", "message", value)
                  }
                />
              )}

              {/* VENDOR */}
              {messageRecipients?.vendor && (
                <MessageRecipientCard
                  accentColor="#ef6c00"
                  roleLabel="Vendor"
                  name={messageRecipients.vendor?.name}
                  mobile={messageRecipients.vendor?.mobile}
                  message={messageRecipients.vendor?.message}
                  included={messageRecipients.vendor?.included !== false}
                  onToggleIncluded={(checked) =>
                    updateSingleRecipient("vendor", "included", checked)
                  }
                  onMobileChange={(value) =>
                    updateSingleRecipient("vendor", "mobile", value)
                  }
                  onMessageChange={(value) =>
                    updateSingleRecipient("vendor", "message", value)
                  }
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            {readyToSendCount} of {totalRecipients} recipient
            {totalRecipients !== 1 ? "s" : ""} ready to send
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={finishAfterMessaging}>Skip</Button>
            <Button
              variant="contained"
              disabled={readyToSendCount === 0}
              onClick={handleSendMessages}
            >
              Send Messages
              {readyToSendCount > 0 ? ` (${readyToSendCount})` : ""}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <PreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewHtml("");
          setPreviewData(null);

          if (pendingMessageRecipientsRef.current) {
            setMessageRecipients(pendingMessageRecipientsRef.current);
            setMessageDialogOpen(true);
            pendingMessageRecipientsRef.current = null;
            return;
          }

          if (fromBooking === true) {
            navigate("/bookings", {
              replace: true,
            });
          } else if (typeof onSuccess === "function") {
            onSuccess();
          } else {
            window.location.href = "/tripsheets";
          }
        }}
        html={previewHtml}
        title="Tripsheet Preview"
        invoiceData={previewData}
        mode="normal"
        templateType="tripsheet"
        showSnackbar={({ message, severity }) => {
          setSnackbar({
            open: true,
            message,
            severity,
          });
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
